export type OverlayPlacement =
    | 'top'
    | 'bottom'
    | 'left'
    | 'right'
    | 'top-start'
    | 'top-end'
    | 'bottom-start'
    | 'bottom-end';

export interface OverlayCoords {
    top: number;
    left: number;
    ax: number;
    ay: number;
}

const GAP = 8;
const ARROW = 5;
const VIEWPORT_PAD = 8;

const clamp = (value: number, min: number, max: number) => Math.min(Math.max(value, min), Math.max(max, min));

/** Anchor a portaled tooltip or popover to a trigger, and keep the arrow on that trigger. */
export const placeOverlay = (
    placement: OverlayPlacement,
    trigger: DOMRect,
    panel: DOMRect,
): OverlayCoords => {
    const anchorX = trigger.left + trigger.width / 2;
    const anchorY = trigger.top + trigger.height / 2;

    let top = 0;
    let left = 0;
    let ax = panel.width / 2 - ARROW;
    let ay = -ARROW;

    switch (placement) {
        case 'top':
            top = trigger.top - panel.height - GAP;
            left = anchorX - panel.width / 2;
            ax = anchorX - left - ARROW;
            ay = panel.height - ARROW;
            break;
        case 'bottom':
            top = trigger.bottom + GAP;
            left = anchorX - panel.width / 2;
            ax = anchorX - left - ARROW;
            ay = -ARROW;
            break;
        case 'left':
            top = anchorY - panel.height / 2;
            left = trigger.left - panel.width - GAP;
            ax = panel.width - ARROW;
            ay = anchorY - top - ARROW;
            break;
        case 'right':
            top = anchorY - panel.height / 2;
            left = trigger.right + GAP;
            ax = -ARROW;
            ay = anchorY - top - ARROW;
            break;
        case 'top-start':
            top = trigger.top - panel.height - GAP;
            left = trigger.left;
            ay = panel.height - ARROW;
            ax = Math.max(10, Math.min(anchorX - left - ARROW, panel.width - 20));
            break;
        case 'top-end':
            top = trigger.top - panel.height - GAP;
            left = trigger.right - panel.width;
            ay = panel.height - ARROW;
            ax = Math.max(10, Math.min(anchorX - left - ARROW, panel.width - 20));
            break;
        case 'bottom-start':
            top = trigger.bottom + GAP;
            left = trigger.left;
            ay = -ARROW;
            ax = Math.max(10, Math.min(anchorX - left - ARROW, panel.width - 20));
            break;
        case 'bottom-end':
            top = trigger.bottom + GAP;
            left = trigger.right - panel.width;
            ay = -ARROW;
            ax = Math.max(10, Math.min(anchorX - left - ARROW, panel.width - 20));
            break;
        default:
            break;
    }

    const maxLeft = window.innerWidth - panel.width - VIEWPORT_PAD;
    const maxTop = window.innerHeight - panel.height - VIEWPORT_PAD;
    const clampedLeft = clamp(left, VIEWPORT_PAD, maxLeft);
    const clampedTop = clamp(top, VIEWPORT_PAD, maxTop);

    return {
        top: clampedTop,
        left: clampedLeft,
        ax: ax - (clampedLeft - left),
        ay: ay - (clampedTop - top),
    };
};
